import { useCallback, useEffect, useRef } from "react"

import { getCoreRowModel, useReactTable } from "@tanstack/react-table"

import { EntityTable } from "@/components/EntityTable"
import { Person } from "@/types/person"

import { peopleColumns } from "./peopleColumns"

interface PeopleDirectoryProps {
  people: Person[]
  selectedPersonId: string | null
  onSelectPerson: (person: Person) => void
  onFetchMore: () => void
  loading: boolean
  hasMorePeople?: boolean
}

const PeopleDirectory = ({
  people,
  selectedPersonId,
  onSelectPerson,
  onFetchMore,
  loading,
  hasMorePeople = true,
}: PeopleDirectoryProps) => {
  const sentinelRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const table = useReactTable({
    data: people,
    columns: peopleColumns,
    getCoreRowModel: getCoreRowModel(),
  })

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting && hasMorePeople && !loading) {
        onFetchMore()
      }
    },
    [hasMorePeople, loading, onFetchMore],
  )

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const observer = new IntersectionObserver(handleIntersection, {
      root: scrollContainerRef.current,
      threshold: 0.1,
    })
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [handleIntersection])

  return (
    <EntityTable
      table={table}
      getRowProps={(person) => ({
        key: person.id,
        dataAttributes: { "data-person-id": person.person_id ?? "" },
        isSelected: selectedPersonId === person.id,
        onClick: (e) => {
          e.stopPropagation()
          onSelectPerson(person)
        },
      })}
      loadingMore={loading}
      hasMore={hasMorePeople}
      scrollContainerRef={scrollContainerRef}
      sentinelRef={sentinelRef}
      footer={`${people.length} ${people.length === 1 ? "person" : "people"} fetched`}
    />
  )
}

export default PeopleDirectory
